import {
  ApprovalMode as ApprovalFlowCellMode,
  type BusinessProcess,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ApprovalFlowProcessSetting } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { BiddingApprovalMode } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

/**
 * 承認フロー設定のセル値 (`bfftypes.v1.ApprovalMode`) を bidding の承認モード
 * (`bffuser.v1.BiddingApprovalMode`) に変換する。
 *
 * 数値は現状揃っている (UNSPECIFIED=0 / APPROVAL=1 / AUTO=2 / NO_APPROVAL=3) が、
 * 由来の異なる enum を暗黙の数値キャストで結び付けず、 変換点をここ 1 か所に閉じる
 * (どちらかの proto が値を追加・並べ替えても破綻しないようにする)。
 */
function toBiddingApprovalMode(
  mode: ApprovalFlowCellMode,
): BiddingApprovalMode {
  switch (mode) {
    case ApprovalFlowCellMode.APPROVAL:
      return BiddingApprovalMode.APPROVAL
    case ApprovalFlowCellMode.AUTO:
      return BiddingApprovalMode.AUTO
    case ApprovalFlowCellMode.NO_APPROVAL:
      return BiddingApprovalMode.NO_APPROVAL
    case ApprovalFlowCellMode.UNSPECIFIED:
    default:
      return BiddingApprovalMode.UNSPECIFIED
  }
}

/**
 * 1 エリアの承認モードを、 承認フロー設定 (`GetApprovalFlowScreen` の
 * `processes`) から解決する。
 *
 * スポット入札 / 時間前入札は断面・スケジュール軸を持たない単一行マトリクス
 * (`docs/specs/admin/operation/approval/approval-flow.md` §1) なので、
 * `section` / `schedule` は常に未設定のセルを探す。 セルが無い (= 個別設定して
 * いないエリア、 または新規テナントで未保存) 場合は spec §1 の新規テナント
 * 既定「承認なし」 を返す (`AdminApprovalFlow` の `convertProcessSetting` と
 * 同じ default)。
 *
 * 提出確認モーダルの確認項目「承認フロー」 (#9586) も、 対象エリアごとの値をこれで引く。
 */
export function resolveAreaApprovalMode(
  processes: readonly ApprovalFlowProcessSetting[],
  businessProcess: BusinessProcess,
  areaCode: AreaCode,
): BiddingApprovalMode {
  const setting = processes.find((p) => p.businessProcess === businessProcess)
  const cell = setting?.cells.find(
    (c) =>
      c.areaCode === areaCode &&
      c.section === undefined &&
      c.schedule === undefined,
  )
  return cell !== undefined
    ? toBiddingApprovalMode(cell.approvalMode)
    : BiddingApprovalMode.NO_APPROVAL
}

/**
 * 承認モードが「承認を経ず提出できる」 side かどうか (AC-BSP-015)。
 *
 * 「承認なし」 に加えて「自動」 もこちら側に含める。 「自動」 のエリアで利用者が
 * 画面から手作業で作成した札は、 自動入札ルールの設定有無にかかわらず「承認なし」
 * と同じ提出操作になる (#8297 の EN 暫定判断 2026-09-19。 PdM の事後確認は #8404、
 * spec への明記は #8407)。 自動入札ルールの実行 (#2511) が入った後も手作業の札は
 * この扱いのままで、 見直しの対象は自動入札ルールが作成する札の側に限る。
 * backend の `ApprovalMode.RequiresApproval()` も「自動」 と「承認なし」 を同じ
 * 承認スキップ側として扱う。
 */
function isApprovalOptionalMode(mode: BiddingApprovalMode): boolean {
  return (
    mode === BiddingApprovalMode.NO_APPROVAL ||
    mode === BiddingApprovalMode.AUTO
  )
}

/**
 * 新規に作成する申請セット (spot の承認申請 / block のグループ) に適用する承認
 * モードを、 対象の札が属するエリア群から解決する (AC-BSP-015)。
 *
 * - 対象エリアの承認モードが全エリアで「承認なし」 または「自動」 (=
 *   [[isApprovalOptionalMode]]、 手作業で作成する札は自動でも承認なし相当。
 *   #8297) に揃っている場合のみ「承認なし」 を返す。
 * - それ以外 (要承認を含む・エリアで承認モードが混在・対象エリア 0 件) は
 *   「要承認」 にとどめる。
 *   ※ エリアで承認モードが混在する場合の扱いは issue #6651 が PdM 未確認の
 *   ため要承認へ倒す (「承認なし」×「自動」 の組合せは両方とも承認スキップ
 *   側なので混在扱いにしない。 「要承認」 が 1 つでも混じる組合せのみ対象)。
 *
 * `GetApprovalFlowScreen` が未取得・取得失敗の間にこの関数を呼んではいけない
 * (2026-09-16 ユーザー決定 1-B)。 以前は「安全側として要承認を返す」フォール
 * バックを持っていたが、 それは「読めていない設定を要承認と推測して確定させる」
 * ことに等しく、 backend 照合 (別 PR、 決定 1-A) が要承認の札を拒否しうる組合せを
 * 黙って作ってしまう。 呼び出し側 ([[SpotBiddingPage]] の `persistDraftBids` /
 * `handleBlockCreate` / `handleBlockUpdate`) が取得未解決の間は呼び出し自体を
 * `ApprovalFlowUnresolvedError` で中断する (= 作成・提出ボタンも押せなくする) ので、
 * `processes` は必ず解決済みの配列として渡ってくる。
 */
export function resolveCreateApprovalMode(params: {
  processes: readonly ApprovalFlowProcessSetting[]
  businessProcess: BusinessProcess
  areaCodes: readonly AreaCode[]
}): BiddingApprovalMode {
  const { processes, businessProcess, areaCodes } = params
  if (areaCodes.length === 0) {
    return BiddingApprovalMode.APPROVAL
  }
  const uniqueAreas = Array.from(new Set(areaCodes))
  const modes = uniqueAreas.map((area) =>
    resolveAreaApprovalMode(processes, businessProcess, area),
  )
  const isUniformApprovalOptional = modes.every(isApprovalOptionalMode)
  return isUniformApprovalOptional
    ? BiddingApprovalMode.NO_APPROVAL
    : BiddingApprovalMode.APPROVAL
}

/**
 * 承認フロー設定 (`GetApprovalFlowScreen`) が未取得・取得失敗のまま新規申請
 * セット (spot Create/Update・block Create/Update) を確定しようとした時に
 * `persistDraftBids` / `handleBlockCreate` / `handleBlockUpdate` が投げる
 * sentinel (2026-09-16 ユーザー決定 1-B)。
 *
 * 読めていない設定を「要承認」 と推測して書き込むフォールバックを廃止した
 * 代わりに、 呼び出し側で書込そのものを中断する。 UI 側は取得中・取得失敗の間
 * 作成・提出ボタンを非活性にする (= 通常はこの sentinel に到達しない) が、
 * 背景で設定取得が失敗に転じてからボタン押下までの間隙を塞ぐ防御としても働く。
 */
export class ApprovalFlowUnresolvedError extends Error {
  constructor() {
    super('approval flow settings are not resolved yet')
    this.name = 'ApprovalFlowUnresolvedError'
  }
}

/**
 * 既に永続化された申請セット群の承認モードが「承認なし」 相当 (= 承認なし /
 * 自動、 [[isApprovalOptionalMode]] 参照) で揃っているかを判定する (= メイン
 * ボタンを「提出」 (直接提出、 承認を経ない SUBMIT) に切り替えてよいか、
 * AC-BSP-015)。
 *
 * 承認モードは作成 (Create) 時に [[resolveCreateApprovalMode]] で確定する。
 * 明細差し替え (Update、 行の編集・削除を含む) でも呼び出し側が
 * [[resolveCreateApprovalMode]] を再度呼んで解決し直した値を送り直し、
 * backend の `WithReplacedBids` はその値で既存の `approvalMode` を上書きする
 * (`spot_bid_approval_request.go` の `WithReplacedBids`、 固定値を引き回さない)。
 * この関数自身は `GetApprovalFlowScreen` を再度引かず、 各申請セットに保存済み
 * (= Create/Update のたびに再解決・確定済み) の `approvalMode` を読むだけでよい。
 * [[resolveCreateApprovalMode]] が「自動」 を「承認なし」 へ畳んで保存するため
 * 実運用では読まないが、 backend が SUBMIT-from-DRAFT のガードで「自動」 も同じ
 * 非承認モードとして扱っている (`ApprovalMode.RequiresApproval()`) ことに合わせ、
 * ここでも「自動」 を direct submit 側として扱う。
 *
 * 対象が 0 件、 または混在 (= 一部だけ承認なし相当) の場合は false (= 現行
 * どおり「承認申請」 フローにとどめる、 安全側)。
 */
export function isDirectSubmitMode(
  modes: readonly BiddingApprovalMode[],
): boolean {
  if (modes.length === 0) return false
  return modes.every(isApprovalOptionalMode)
}
